import { useCallback, useState, useSyncExternalStore } from 'react';
import {
  applyUpdate,
  isUpdateHeldByRun,
  shouldPromptUpdate,
  subscribeSwUpdate,
} from '../app/swUpdate';
import { errorMessage } from '../app/errors';
import { log } from './fatalBridge';

/**
 * "Update ready · tap to load."
 *
 * Rendered at the very top of the shell, above the unit switcher, because a stale instrument
 * produces confidently wrong numbers — the failure mode is not inconvenience, it is a measurement
 * you trust and should not. It gets the same visual weight as the ENV-1 banner for the same reason.
 *
 * While a run holds the lock the actionable prompt is withheld (applying it reloads the page and
 * would destroy the run) and a quiet line explains why, so the operator is not left wondering
 * whether the update arrived. It becomes tappable by itself the moment the run releases.
 */
export function UpdateBanner() {
  const ready = useSyncExternalStore(subscribeSwUpdate, shouldPromptUpdate, () => false);
  const held = useSyncExternalStore(subscribeSwUpdate, isUpdateHeldByRun, () => false);
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);

  const onApply = useCallback(() => {
    setError(null);
    setApplying(true);
    void applyUpdate().catch((cause) => {
      // The reload never happened, so the operator has to be told — there is no console at a venue.
      log.exception('sw', 'applying the update failed', cause);
      setError(errorMessage(cause));
      setApplying(false);
    });
  }, []);

  if (held) {
    return (
      <div className="update-banner held" role="status">
        <strong>Update ready — waiting for this run to finish.</strong>
        <p className="note">
          Loading it reloads the app, which would destroy the run in progress. It will offer itself
          as soon as the run ends.
        </p>
      </div>
    );
  }

  if (!ready) return null;

  return (
    <div className="update-banner" role="alert">
      <strong>Update ready</strong>
      <p className="note">
        This app is running an older bundle. Load the update before measuring — a stale instrument
        produces numbers that look fine and are not.
      </p>
      {error && <div className="error">{error}</div>}
      <button className="primary" onClick={onApply} disabled={applying}>
        {applying ? 'Loading…' : 'Tap to load the update'}
      </button>
    </div>
  );
}
